import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { HelperProfile, JobPosting } from '../types';
import { apiService } from '../services/api';
import { useAuth } from './AuthContext';
import i18n from '../i18n/config';
import { resolveApiError } from '../utils/i18n';

interface BookmarkContextType {
  bookmarks: HelperProfile[];
  bookmarkCount: number;
  isBookmarked: (helperId: number) => boolean;
  toggleBookmark: (helper: HelperProfile) => Promise<void>;
  // Job Post bookmarks
  jobPostBookmarks: JobPosting[];
  jobPostBookmarkCount: number;
  isJobPostBookmarked: (jobPostId: number) => boolean;
  toggleJobPostBookmark: (jobPost: JobPosting) => Promise<void>;
  // Loading states
  isLoading: boolean;
  error: string | null;
  clearError: () => void;
}

const BookmarkContext = createContext<BookmarkContextType | undefined>(undefined);
const STORAGE_KEY = 'bookmarked-helpers';
const JOB_POST_STORAGE_KEY = 'bookmarked-job-posts';

export const BookmarkProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [bookmarks, setBookmarks] = useState<HelperProfile[]>([]);
  const [jobPostBookmarks, setJobPostBookmarks] = useState<JobPosting[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const persist = (key: string, items: unknown[]) => { try { localStorage.setItem(key, JSON.stringify(items)); } catch {} };

  // Load candidate bookmarks from backend and localStorage (EMPLOYER only)
  useEffect(() => {
    // Skip if auth is still loading, user not logged in, or not EMPLOYER role
    if (authLoading || !user?.id || user?.role !== 'EMPLOYER') return;
    
    const loadBookmarks = async () => {
      try {
        setIsLoading(true);
        
        // First, load from localStorage as cache
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed: HelperProfile[] = JSON.parse(stored);
          const uniqueBookmarks = parsed.filter((bookmark, index, self) =>
            index === self.findIndex((b) => b.id === bookmark.id)
          );
          setBookmarks(uniqueBookmarks);
          if (uniqueBookmarks.length !== parsed.length) {
            persist(STORAGE_KEY,uniqueBookmarks);
          }
        }

        // Backend as single source of truth
        const response = await apiService.candidateBookmarks.getMyBookmarks();
        if (response.success && response.data) {
          setBookmarks(response.data);
          persist(STORAGE_KEY,response.data);
        }
      } catch (err) {
        console.error('Failed to load candidate bookmarks from backend', err);
        // Keep localStorage data as fallback
        setError(i18n.t('bookmark.loadFailed', '無法載入收藏資料，請稍後再試'));
      } finally {
        setIsLoading(false);
      }
    };
    loadBookmarks();
  }, [authLoading, user?.id, user?.role]);

  // Load job post bookmarks from backend and localStorage (HELPER only)
  useEffect(() => {
    // Skip if auth is still loading, user not logged in, or not HELPER role
    if (authLoading || !user?.id || user?.role !== 'HELPER') return;
    
    const loadJobPostBookmarks = async () => {
      try {
        setIsLoading(true);
        
        // First, load from localStorage as cache
        const stored = localStorage.getItem(JOB_POST_STORAGE_KEY);
        if (stored) {
          const parsed: JobPosting[] = JSON.parse(stored);
          const uniqueBookmarks = parsed.filter((bookmark, index, self) =>
            index === self.findIndex((b) => b.id === bookmark.id)
          );
          setJobPostBookmarks(uniqueBookmarks);
          if (uniqueBookmarks.length !== parsed.length) {
            persist(JOB_POST_STORAGE_KEY,uniqueBookmarks);
          }
        }

        // Backend as single source of truth
        const response = await apiService.jobBookmarks.getMyBookmarks();
        if (response.success && response.data) {
          setJobPostBookmarks(response.data);
          persist(JOB_POST_STORAGE_KEY,response.data);
        }
      } catch (err) {
        console.error('Failed to load job post bookmarks from backend', err);
        // Keep localStorage data as fallback
        setError(i18n.t('bookmark.jobLoadFailed', '無法載入工作收藏資料，請稍後再試'));
      } finally {
        setIsLoading(false);
      }
    };
    loadJobPostBookmarks();
  }, [authLoading, user?.id, user?.role]);

  const isBookmarked = (helperId: number) => bookmarks.some((bookmark) => bookmark.id === helperId);

  const toggleBookmark = async (helper: HelperProfile) => {
    if (!user) {
      sessionStorage.setItem('returnUrl', window.location.pathname);
      navigate('/register');
      return;
    }
    const prevBookmarks = [...bookmarks];
    const isAlreadyBookmarked = bookmarks.some((b) => b.id === helper.id);
    
    // Optimistic update
    let newBookmarks: HelperProfile[];
    if (isAlreadyBookmarked) {
      newBookmarks = bookmarks.filter((b) => b.id !== helper.id);
    } else {
      newBookmarks = [...bookmarks, helper];
    }
    setBookmarks(newBookmarks);
    persist(STORAGE_KEY,newBookmarks);

    try {
      if (isAlreadyBookmarked) {
        const response = await apiService.candidateBookmarks.remove(helper.id);
        if (!response.success) {
          setBookmarks(prevBookmarks);
          persist(STORAGE_KEY,prevBookmarks);
          setError(resolveApiError(response.errorCode, response.message, (key: string) => i18n.t(key)));
        }
      } else {
        const response = await apiService.candidateBookmarks.add(helper.id);
        if (!response.success) {
          setBookmarks(prevBookmarks);
          persist(STORAGE_KEY,prevBookmarks);
          setError(resolveApiError(response.errorCode, response.message, (key: string) => i18n.t(key)));
        }
      }
    } catch (err) {
      console.error('Failed to toggle bookmark', err);
      setBookmarks(prevBookmarks);
      persist(STORAGE_KEY,prevBookmarks);
      setError(i18n.t('bookmark.operationFailed', '操作失敗，請稍後再試'));
    }
  };

  const isJobPostBookmarked = (jobPostId: number) => jobPostBookmarks.some((bookmark) => bookmark.id === jobPostId);

  const toggleJobPostBookmark = async (jobPost: JobPosting) => {
    if (!user) {
      sessionStorage.setItem('returnUrl', window.location.pathname);
      navigate('/register');
      return;
    }
    const prevJobPostBookmarks = [...jobPostBookmarks];
    const isAlreadyBookmarked = jobPostBookmarks.some((b) => b.id === jobPost.id);
    
    // Optimistic update
    let newBookmarks: JobPosting[];
    if (isAlreadyBookmarked) {
      newBookmarks = jobPostBookmarks.filter((b) => b.id !== jobPost.id);
    } else {
      newBookmarks = [...jobPostBookmarks, jobPost];
    }
    setJobPostBookmarks(newBookmarks);
    persist(JOB_POST_STORAGE_KEY,newBookmarks);

    try {
      if (isAlreadyBookmarked) {
        const response = await apiService.jobBookmarks.remove(jobPost.id);
        if (!response.success) {
          setJobPostBookmarks(prevJobPostBookmarks);
          persist(JOB_POST_STORAGE_KEY,prevJobPostBookmarks);
          setError(resolveApiError(response.errorCode, response.message, (key: string) => i18n.t(key)));
        }
      } else {
        const response = await apiService.jobBookmarks.add(jobPost.id);
        if (!response.success) {
          setJobPostBookmarks(prevJobPostBookmarks);
          persist(JOB_POST_STORAGE_KEY,prevJobPostBookmarks);
          setError(resolveApiError(response.errorCode, response.message, (key: string) => i18n.t(key)));
        }
      }
    } catch (err: unknown) {
      console.error('Failed to toggle job post bookmark', err);
      setJobPostBookmarks(prevJobPostBookmarks);
      persist(JOB_POST_STORAGE_KEY,prevJobPostBookmarks);
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || i18n.t('bookmark.operationFailed', '操作失敗，請稍後再試'));
    }
  };

  const bookmarkCount = bookmarks.length;
  const jobPostBookmarkCount = jobPostBookmarks.length;

  const value: BookmarkContextType = useMemo(
    () => ({
      bookmarks,
      bookmarkCount,
      isBookmarked,
      toggleBookmark,
      jobPostBookmarks,
      jobPostBookmarkCount,
      isJobPostBookmarked,
      toggleJobPostBookmark,
      isLoading,
      error,
      clearError,
    }),
    [bookmarks, bookmarkCount, jobPostBookmarks, jobPostBookmarkCount, isLoading, error]
  );

  return <BookmarkContext.Provider value={value}>{children}</BookmarkContext.Provider>;
};

export const useBookmarks = () => {
  const context = useContext(BookmarkContext);
  if (context === undefined) {
    throw new Error('useBookmarks must be used within a BookmarkProvider');
  }
  return context;
};
